import { Link, useLocation } from 'react-router-dom'
import { useAlerts, useIncidents } from '@/api/queries'
import { cn } from '@/lib/utils'
import { NAV_GROUPS } from './nav-items'
import { useShell } from './shell-context'

function isNavItemActive(to: string, pathname: string, hash: string) {
  const [toPath, toHash] = to.split('#')
  if (toPath !== pathname) return false
  return toHash ? hash === `#${toHash}` : hash === ''
}

export function LeftNav() {
  const { navOpen, setNavOpen } = useShell()
  const location = useLocation()

  // The Alerts nav badge is the count of alerts still needing attention
  // (firing or acked but not yet resolved/silenced) — real data (r1-alerting),
  // not the earlier hardcoded stub. Undefined while loading, an honest blank
  // rather than a fabricated placeholder number.
  const { data: alertsData } = useAlerts()
  const openAlertCount = alertsData?.alerts.filter((a) => a.status === 'firing' || a.status === 'acked').length

  // The Incidents nav badge is the count of incidents not yet resolved —
  // real data (r1-incidents), not the earlier hardcoded stub.
  const { data: incidentsData } = useIncidents()
  const activeIncidentCount = incidentsData?.incidents.filter((i) => i.status !== 'resolved').length

  return (
    <nav
      className={cn(
        'fixed top-14 bottom-0 left-0 z-[400] overflow-y-auto border-r border-border bg-surface transition-[width]',
        navOpen ? 'w-[220px] p-2' : 'w-[52px] p-1.5',
      )}
    >
      {NAV_GROUPS.map((group, i) => (
        <div key={group.label ?? i} className="mb-1">
          {group.label && navOpen && (
            <div className="px-2.5 pt-2.5 pb-1 text-[10.5px] font-bold tracking-wide text-unknown uppercase">
              {group.label}
            </div>
          )}
          {group.items.map((item) => {
            const active = isNavItemActive(item.to, location.pathname, location.hash)
            const count = item.to === '/alerts' ? openAlertCount : item.to === '/incidents' ? activeIncidentCount : item.count
            return (
              <Link
                key={item.to}
                to={item.to}
                title={item.label}
                className={cn(
                  'my-px flex items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] text-ink hover:bg-canvas',
                  item.sub && 'pl-5 text-[12.5px] text-ink-2',
                  active && 'bg-accent-weak font-semibold text-accent',
                  !navOpen && 'justify-center overflow-hidden text-[11px]',
                )}
              >
                <span>{navOpen ? item.label : item.label.slice(0, 2)}</span>
                {navOpen && count !== undefined && (
                  <span className="font-mono text-[11px] text-ink-2">{count}</span>
                )}
              </Link>
            )
          })}
        </div>
      ))}
      <button
        onClick={() => setNavOpen(!navOpen)}
        className="mt-2 w-full cursor-pointer rounded-md px-2.5 py-1.5 text-left text-[11px] text-ink-2 hover:bg-canvas"
        aria-label={navOpen ? 'Collapse navigation' : 'Expand navigation'}
      >
        {navOpen ? '« collapse' : '»'}
      </button>
    </nav>
  )
}
